import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useUser, ClerkProvider } from '@clerk/react';
import '@/App.css';
import PageLoader from './components/PageLoader';
import NavigationProgress from './components/NavigationProgress';
import CanonicalUrl from './components/CanonicalUrl';
import CookieConsent from './components/CookieConsent';
import SalesIQIdentity from './components/SalesIQIdentity';
import ScrollToTop from './components/ScrollToTop';
import WebMCPManager from './components/WebMCPManager';
import { Toaster } from 'sonner';
import { ThemeProvider } from '@/components/ThemeProvider';
import { UserProvider } from './contexts/UserContext';

import LandingPage from './pages/LandingPage';
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const BillingPage = React.lazy(() => import('./pages/BillingPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const ApiDocsPage = React.lazy(() => import('./pages/ApiDocsPage'));
const CustomDomainPage = React.lazy(() => import('./pages/CustomDomainPage'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = React.lazy(() => import('./pages/TermsConditions'));
const RefundPolicy = React.lazy(() => import('./pages/RefundPolicy'));
const IntegrationPage = React.lazy(() => import('./pages/IntegrationPage'));
const IntegrationsPage = React.lazy(() => import('./pages/IntegrationsPage'));
const SecurityPage = React.lazy(() => import('./pages/SecurityPage'));
const GoogleDriveDirectLinkGeneratorPage = React.lazy(() => import('./pages/GoogleDriveDirectLinkGeneratorPage'));
const PublicApiDocsPage = React.lazy(() => import('./pages/PublicApiDocsPage'));
const PublicIntegrationsPage = React.lazy(() => import('./pages/PublicIntegrationsPage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const ThankYouPage = React.lazy(() => import('./pages/ThankYouPage'));
const OfferingLandingPage = React.lazy(() => import('./pages/OfferingLandingPage'));
const McpServerPage = React.lazy(() => import('./pages/McpServerPage'));
const DashboardMcpPage = React.lazy(() => import('./pages/DashboardMcpPage'));

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

function ProtectedRoute({ children }) {
  const { isLoaded, isSignedIn } = useUser();

  if (!isLoaded) {
    return <PageLoader message="Authenticating..." />;
  }

  return isSignedIn ? <>{children}</> : <Navigate to="/" replace />;
}

function AppContent() {
  return (
    <>
      <ScrollToTop />
      <NavigationProgress />
      <CanonicalUrl />
      <Suspense fallback={<PageLoader message="Loading page..." />}>
        <Routes>
          <Route
            path="/"
            element={<LandingPage />}
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/custom-domain"
            element={
              <ProtectedRoute>
                <CustomDomainPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/api-docs" element={<PublicApiDocsPage />} />
          <Route 
            path="/dashboard/api-docs" 
            element={
              <ProtectedRoute>
                <ApiDocsPage />
              </ProtectedRoute>
            } 
          />
          <Route path="/integrations" element={<PublicIntegrationsPage />} />
          <Route 
            path="/dashboard/integrations" 
            element={
              <ProtectedRoute>
                <IntegrationsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/mcp" 
            element={
              <ProtectedRoute>
                <DashboardMcpPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/mcp-server" 
            element={
              <ProtectedRoute>
                <DashboardMcpPage />
              </ProtectedRoute>
            } 
          />
          <Route path="/integration" element={<Navigate to="/integrations" replace />} />
          <Route path="/google-drive-direct-link-generator" element={<GoogleDriveDirectLinkGeneratorPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/mcp-server" element={<McpServerPage />} />
          <Route path="/image-to-url" element={<OfferingLandingPage />} />
          <Route path="/video-to-url" element={<OfferingLandingPage />} />
          <Route path="/pdf-to-url" element={<OfferingLandingPage />} />
          <Route path="/audio-to-url" element={<OfferingLandingPage />} />
          <Route path="/html-to-url" element={<OfferingLandingPage />} />
          <Route path="/vcf-to-url" element={<OfferingLandingPage />} />
          <Route path="/hex-to-url" element={<OfferingLandingPage />} />
          <Route path="/srt-to-url" element={<OfferingLandingPage />} />
          <Route path="/vtt-to-url" element={<OfferingLandingPage />} />
          <Route path="/ttf-to-url" element={<OfferingLandingPage />} />
          <Route path="/:slug" element={<IntegrationPage />} />
        </Routes>
      </Suspense>
      <Toaster position="top-right" />
      <SalesIQIdentity />
      <CookieConsent />
      <WebMCPManager />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ClerkProvider
        publishableKey={clerkPubKey}
        signInFallbackRedirectUrl="/dashboard"
        signUpFallbackRedirectUrl="/dashboard"
      >
        <ThemeProvider defaultTheme="system" storageKey="upload-to-url-theme">
          <UserProvider>
            <AppContent />
          </UserProvider>
        </ThemeProvider>
      </ClerkProvider>
    </BrowserRouter>
  );
}

export default App;